var _queryCache, _mutationCache, _defaultOptions, _queryDefaults, _mutationDefaults, _mountCount, _unsubscribeFocus, _unsubscribeOnline;
function _classPrivateFieldInitSpec(e, t, a) { _checkPrivateRedeclaration(e, t), t.set(e, a); }
function _checkPrivateRedeclaration(e, t) { if (t.has(e)) throw new TypeError("Cannot initialize the same private elements twice on an object"); }
function _classPrivateFieldGet(s, a) { return s.get(_assertClassBrand(s, a)); }
function _classPrivateFieldSet(s, a, r) { return s.set(_assertClassBrand(s, a), r), r; }
function _assertClassBrand(e, t, n) { if ("function" == typeof e ? e === t : e.has(t)) return arguments.length < 3 ? t : n; throw new TypeError("Private element is not present on this object"); }
// src/queryClient.ts
import { functionalUpdate, hashKey, hashQueryKeyByOptions, noop, partialMatchKey, resolveStaleTime, skipToken } from "./utils.js";
import { QueryCache } from "./queryCache.js";
import { MutationCache } from "./mutationCache.js";
import { focusManager } from "./focusManager.js";
import { onlineManager } from "./onlineManager.js";
import { notifyManager } from "./notifyManager.js";
import { infiniteQueryBehavior } from "./infiniteQueryBehavior.js";
var QueryClient = (_queryCache = /*#__PURE__*/new WeakMap(), _mutationCache = /*#__PURE__*/new WeakMap(), _defaultOptions = /*#__PURE__*/new WeakMap(), _queryDefaults = /*#__PURE__*/new WeakMap(), _mutationDefaults = /*#__PURE__*/new WeakMap(), _mountCount = /*#__PURE__*/new WeakMap(), _unsubscribeFocus = /*#__PURE__*/new WeakMap(), _unsubscribeOnline = /*#__PURE__*/new WeakMap(), class QueryClient {
  constructor() {
    let config = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
    _classPrivateFieldInitSpec(this, _queryCache, void 0);
    _classPrivateFieldInitSpec(this, _mutationCache, void 0);
    _classPrivateFieldInitSpec(this, _defaultOptions, void 0);
    _classPrivateFieldInitSpec(this, _queryDefaults, void 0);
    _classPrivateFieldInitSpec(this, _mutationDefaults, void 0);
    _classPrivateFieldInitSpec(this, _mountCount, void 0);
    _classPrivateFieldInitSpec(this, _unsubscribeFocus, void 0);
    _classPrivateFieldInitSpec(this, _unsubscribeOnline, void 0);
    _classPrivateFieldSet(_queryCache, this, config.queryCache || new QueryCache());
    _classPrivateFieldSet(_mutationCache, this, config.mutationCache || new MutationCache());
    _classPrivateFieldSet(_defaultOptions, this, config.defaultOptions || {});
    _classPrivateFieldSet(_queryDefaults, this, /* @__PURE__ */new Map());
    _classPrivateFieldSet(_mutationDefaults, this, /* @__PURE__ */new Map());
    _classPrivateFieldSet(_mountCount, this, 0);
  }
  mount() {
    var _this$mountCount, _this$mountCount2;
    _classPrivateFieldSet(_mountCount, this, (_this$mountCount = _classPrivateFieldGet(_mountCount, this), _this$mountCount2 = _this$mountCount++, _this$mountCount)), _this$mountCount2;
    if (_classPrivateFieldGet(_mountCount, this) !== 1) return;
    _classPrivateFieldSet(_unsubscribeFocus, this, focusManager.subscribe(async focused => {
      if (focused) {
        await this.resumePausedMutations();
        _classPrivateFieldGet(_queryCache, this).onFocus();
      }
    }));
    _classPrivateFieldSet(_unsubscribeOnline, this, onlineManager.subscribe(async online => {
      if (online) {
        await this.resumePausedMutations();
        _classPrivateFieldGet(_queryCache, this).onOnline();
      }
    }));
  }
  unmount() {
    var _this$mountCount3, _this$mountCount4, _classPrivateFieldGet2, _classPrivateFieldGet3;
    _classPrivateFieldSet(_mountCount, this, (_this$mountCount3 = _classPrivateFieldGet(_mountCount, this), _this$mountCount4 = _this$mountCount3--, _this$mountCount3)), _this$mountCount4;
    if (_classPrivateFieldGet(_mountCount, this) !== 0) return;
    (_classPrivateFieldGet2 = _classPrivateFieldGet(_unsubscribeFocus, this)) === null || _classPrivateFieldGet2 === void 0 || _classPrivateFieldGet2.call(this);
    _classPrivateFieldSet(_unsubscribeFocus, this, void 0);
    (_classPrivateFieldGet3 = _classPrivateFieldGet(_unsubscribeOnline, this)) === null || _classPrivateFieldGet3 === void 0 || _classPrivateFieldGet3.call(this);
    _classPrivateFieldSet(_unsubscribeOnline, this, void 0);
  }
  isFetching(filters) {
    return _classPrivateFieldGet(_queryCache, this).findAll({
      ...filters,
      fetchStatus: "fetching"
    }).length;
  }
  isMutating(filters) {
    return _classPrivateFieldGet(_mutationCache, this).findAll({
      ...filters,
      status: "pending"
    }).length;
  }
  /**
   * Imperative (non-reactive) way to retrieve data for a QueryKey.
   * Should only be used in callbacks or functions where reading the latest data is necessary, e.g. for optimistic updates.
   *
   * Hint: Do not use this function inside a component, because it won't receive updates.
   * Use `useQuery` to create a `QueryObserver` that subscribes to changes.
   */
  getQueryData(queryKey) {
    var _classPrivateFieldGet4;
    const options = this.defaultQueryOptions({
      queryKey
    });
    return (_classPrivateFieldGet4 = _classPrivateFieldGet(_queryCache, this).get(options.queryHash)) === null || _classPrivateFieldGet4 === void 0 ? void 0 : _classPrivateFieldGet4.state.data;
  }
  ensureQueryData(options) {
    const defaultedOptions = this.defaultQueryOptions(options);
    const query = _classPrivateFieldGet(_queryCache, this).build(this, defaultedOptions);
    const cachedData = query.state.data;
    if (cachedData === void 0) {
      return this.fetchQuery(options);
    }
    if (options.revalidateIfStale && query.isStaleByTime(resolveStaleTime(defaultedOptions.staleTime, query))) {
      void this.prefetchQuery(defaultedOptions);
    }
    return Promise.resolve(cachedData);
  }
  getQueriesData(filters) {
    return _classPrivateFieldGet(_queryCache, this).findAll(filters).map(_ref => {
      let {
        queryKey,
        state
      } = _ref;
      const data = state.data;
      return [queryKey, data];
    });
  }
  setQueryData(queryKey, updater, options) {
    const defaultedOptions = this.defaultQueryOptions({
      queryKey
    });
    const query = _classPrivateFieldGet(_queryCache, this).get(defaultedOptions.queryHash);
    const prevData = query === null || query === void 0 ? void 0 : query.state.data;
    const data = functionalUpdate(updater, prevData);
    if (data === void 0) {
      return void 0;
    }
    return _classPrivateFieldGet(_queryCache, this).build(this, defaultedOptions).setData(data, {
      ...options,
      manual: true
    });
  }
  setQueriesData(filters, updater, options) {
    return notifyManager.batch(() => _classPrivateFieldGet(_queryCache, this).findAll(filters).map(_ref2 => {
      let {
        queryKey
      } = _ref2;
      return [queryKey, this.setQueryData(queryKey, updater, options)];
    }));
  }
  getQueryState(queryKey) {
    var _classPrivateFieldGet5;
    const options = this.defaultQueryOptions({
      queryKey
    });
    return (_classPrivateFieldGet5 = _classPrivateFieldGet(_queryCache, this).get(options.queryHash)) === null || _classPrivateFieldGet5 === void 0 ? void 0 : _classPrivateFieldGet5.state;
  }
  removeQueries(filters) {
    const queryCache = _classPrivateFieldGet(_queryCache, this);
    notifyManager.batch(() => {
      queryCache.findAll(filters).forEach(query => {
        queryCache.remove(query);
      });
    });
  }
  resetQueries(filters, options) {
    const queryCache = _classPrivateFieldGet(_queryCache, this);
    return notifyManager.batch(() => {
      queryCache.findAll(filters).forEach(query => {
        query.reset();
      });
      return this.refetchQueries({
        type: "active",
        ...filters
      }, options);
    });
  }
  cancelQueries(filters) {
    let cancelOptions = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
    const defaultedCancelOptions = {
      revert: true,
      ...cancelOptions
    };
    const promises = notifyManager.batch(() => _classPrivateFieldGet(_queryCache, this).findAll(filters).map(query => query.cancel(defaultedCancelOptions)));
    return Promise.all(promises).then(noop).catch(noop);
  }
  invalidateQueries(filters) {
    let options = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
    return notifyManager.batch(() => {
      var _ref3, _filters$refetchType;
      _classPrivateFieldGet(_queryCache, this).findAll(filters).forEach(query => {
        query.invalidate();
      });
      if ((filters === null || filters === void 0 ? void 0 : filters.refetchType) === "none") {
        return Promise.resolve();
      }
      return this.refetchQueries({
        ...filters,
        type: (_ref3 = (_filters$refetchType = filters === null || filters === void 0 ? void 0 : filters.refetchType) !== null && _filters$refetchType !== void 0 ? _filters$refetchType : filters === null || filters === void 0 ? void 0 : filters.type) !== null && _ref3 !== void 0 ? _ref3 : "active"
      }, options);
    });
  }
  refetchQueries(filters) {
    var _options$cancelRefetc;
    let options = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
    const fetchOptions = {
      ...options,
      cancelRefetch: (_options$cancelRefetc = options.cancelRefetch) !== null && _options$cancelRefetc !== void 0 ? _options$cancelRefetc : true
    };
    const promises = notifyManager.batch(() => _classPrivateFieldGet(_queryCache, this).findAll(filters).filter(query => !query.isDisabled() && !query.isStatic()).map(query => {
      let promise = query.fetch(void 0, fetchOptions);
      if (!fetchOptions.throwOnError) {
        promise = promise.catch(noop);
      }
      return query.state.fetchStatus === "paused" ? Promise.resolve() : promise;
    }));
    return Promise.all(promises).then(noop);
  }
  fetchQuery(options) {
    const defaultedOptions = this.defaultQueryOptions(options);
    if (defaultedOptions.retry === void 0) {
      defaultedOptions.retry = false;
    }
    const query = _classPrivateFieldGet(_queryCache, this).build(this, defaultedOptions);
    return query.isStaleByTime(resolveStaleTime(defaultedOptions.staleTime, query)) ? query.fetch(defaultedOptions) : Promise.resolve(query.state.data);
  }
  prefetchQuery(options) {
    return this.fetchQuery(options).then(noop).catch(noop);
  }
  fetchInfiniteQuery(options) {
    options.behavior = infiniteQueryBehavior(options.pages);
    return this.fetchQuery(options);
  }
  prefetchInfiniteQuery(options) {
    return this.fetchInfiniteQuery(options).then(noop).catch(noop);
  }
  ensureInfiniteQueryData(options) {
    options.behavior = infiniteQueryBehavior(options.pages);
    return this.ensureQueryData(options);
  }
  resumePausedMutations() {
    if (onlineManager.isOnline()) {
      return _classPrivateFieldGet(_mutationCache, this).resumePausedMutations();
    }
    return Promise.resolve();
  }
  getQueryCache() {
    return _classPrivateFieldGet(_queryCache, this);
  }
  getMutationCache() {
    return _classPrivateFieldGet(_mutationCache, this);
  }
  getDefaultOptions() {
    return _classPrivateFieldGet(_defaultOptions, this);
  }
  setDefaultOptions(options) {
    _classPrivateFieldSet(_defaultOptions, this, options);
  }
  setQueryDefaults(queryKey, options) {
    _classPrivateFieldGet(_queryDefaults, this).set(hashKey(queryKey), {
      queryKey,
      defaultOptions: options
    });
  }
  getQueryDefaults(queryKey) {
    const defaults = [..._classPrivateFieldGet(_queryDefaults, this).values()];
    const result = {};
    defaults.forEach(queryDefault => {
      if (partialMatchKey(queryKey, queryDefault.queryKey)) {
        Object.assign(result, queryDefault.defaultOptions);
      }
    });
    return result;
  }
  setMutationDefaults(mutationKey, options) {
    _classPrivateFieldGet(_mutationDefaults, this).set(hashKey(mutationKey), {
      mutationKey,
      defaultOptions: options
    });
  }
  getMutationDefaults(mutationKey) {
    const defaults = [..._classPrivateFieldGet(_mutationDefaults, this).values()];
    const result = {};
    defaults.forEach(queryDefault => {
      if (partialMatchKey(mutationKey, queryDefault.mutationKey)) {
        Object.assign(result, queryDefault.defaultOptions);
      }
    });
    return result;
  }
  defaultQueryOptions(options) {
    if (options._defaulted) {
      return options;
    }
    const defaultedOptions = {
      ..._classPrivateFieldGet(_defaultOptions, this).queries,
      ...this.getQueryDefaults(options.queryKey),
      ...options,
      _defaulted: true
    };
    if (!defaultedOptions.queryHash) {
      defaultedOptions.queryHash = hashQueryKeyByOptions(defaultedOptions.queryKey, defaultedOptions);
    }
    if (defaultedOptions.refetchOnReconnect === void 0) {
      defaultedOptions.refetchOnReconnect = defaultedOptions.networkMode !== "always";
    }
    if (defaultedOptions.throwOnError === void 0) {
      defaultedOptions.throwOnError = !!defaultedOptions.suspense;
    }
    if (!defaultedOptions.networkMode && defaultedOptions.persister) {
      defaultedOptions.networkMode = "offlineFirst";
    }
    if (defaultedOptions.queryFn === skipToken) {
      defaultedOptions.enabled = false;
    }
    return defaultedOptions;
  }
  defaultMutationOptions(options) {
    if (options !== null && options !== void 0 && options._defaulted) {
      return options;
    }
    return {
      ..._classPrivateFieldGet(_defaultOptions, this).mutations,
      ...((options === null || options === void 0 ? void 0 : options.mutationKey) && this.getMutationDefaults(options.mutationKey)),
      ...options,
      _defaulted: true
    };
  }
  clear() {
    _classPrivateFieldGet(_queryCache, this).clear();
    _classPrivateFieldGet(_mutationCache, this).clear();
  }
});
export { QueryClient };